<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1>All Providers</h1>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Latest Entries
            <v-spacer />
            <v-text-field v-model="search"
                          append-icon="search"
                          label="Search"
                          single-line
                          hide-details
                          @input="page = 1" />
          </v-card-title>
          <v-data-table class="elevation-1"
                        sort-by="CreatedOn"
                        sort-desc
                        :disable-pagination="items.length < 10"
                        :hide-default-footer="items.length < 10"
                        :loading="loading"
                        :headers="headers"
                        :items="items"
                        :footer-props="{
                          itemsPerPageOptions: [],
                          showFirstLastPage: true,
                        }"
                        :search="search"
                        :page.sync="page">
            <template v-slot:item.PhotoId="{ item }">
              <v-img width="30px"
                     height="30px"
                     :src="getMedia(item.Id)" />
            </template>
            <template v-slot:item.CreatedOn="{ item }">
              {{ friendlyDate(item.CreatedOn) }}
            </template>
            <template v-slot:item.Active="{ item }">
              <v-switch @change="$refs.confirmActivation.open({ value: $event, target: item })"
                        v-model="item.Active"
                        :disabled="!$store.getters.can('Editor')">
                {{ item.Active ? 'Active' : 'Inactive' }}
              </v-switch>
            </template>
            <template v-slot:item.Status="{ item }">
              {{ friendlyStatus(item.Status) }}
            </template>
            <template v-slot:item.Actions="{ item }">
              <v-tooltip top :activator="`#pdfBtn-${item.Id}`">
                Download PDF
              </v-tooltip>
              <v-tooltip top :activator="`#editBtn-${item.Id}`">
                {{ $store.getters.can('Editor') || item.Status === 0 ? 'Edit' : 'View' }}
              </v-tooltip>
              <v-tooltip top :activator="`#deleteBtn-${item.Id}`">
                Delete
              </v-tooltip>
              <v-btn icon link
                     color="accent"
                     :id="`pdfBtn-${item.Id}`"
                     @click="downloadPdf(item)">
                <v-icon>get_app</v-icon>
              </v-btn>
              <v-btn icon link
                     color="accent"
                     :id="`editBtn-${item.Id}`"
                     :to="{ name: 'provider.update', params: { id: item.Id } }">
                <v-icon>
                  {{ $store.getters.can('Editor') || item.Status === 0
                  ? 'edit'
                  : 'visibility' }}
                </v-icon>
              </v-btn>
              <v-btn icon link
                     color="accent"
                     v-if="$store.getters.can('Admin')"
                     :id="`deleteBtn-${item.Id}`"
                     @click="$refs.confirmDelete.open(item)">
                <v-icon>delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" class="text-right">
        <v-btn color="primary" dark link :to="{ name: 'provider.create' }">
          <v-icon dark left>add</v-icon>
          Create
        </v-btn>
      </v-col>
    </v-row>
    <v-row justify="center" align="center">
      <ConfirmDialog ref="confirmActivation"
                     title="CONFIRM PROVIDER UPDATE"
                     width="450"
                     @cancel="$event.target.Active = !$event.target.Active"
                     @continue="updateActiveState($event)">
        <template v-slot:default="{ item }">
          This action will <strong>{{ item && item.value ? 'Activate' : 'Deactivate' }}</strong>
          the selected Provider<br><br>
          <span v-if="item && item.value">
            This record will now be available to general consumers of the FAP API<br>
          </span>
          <span v-else>
            This record will no longer be available to general consumers of the FAP API<br>
            You may still make changes to this record within the FAP UI
            and reactivate when ready<br>
          </span>
          <strong>Are you sure you would like to proceed?</strong>
        </template>
      </ConfirmDialog>
      <ConfirmDialog ref="confirmDelete"
                     title="WARNING"
                     width="450"
                     @continue="deleteItem($event)">
        This action will permanently delete this provider
        and remove all references from Locations that may use it.<br>
        <strong>Are you sure you would like to proceed?</strong>
      </ConfirmDialog>
    </v-row>
  </v-container>
</template>

<script>
import FriendlyFields from '@/components/Mixins/FriendlyFields';
import { mediaHelpers } from '@/util/Helpers';

export default {
  name: 'ProviderIndex',
  mixins: [
    FriendlyFields,
  ],
  data() {
    return {
      headers: [
        { text: 'Id', value: 'Id' },
        {
          text: 'Photo',
          value: 'PhotoId',
          filterable: false,
          sortable: false,
        },
        { text: 'First Name', value: 'FirstName' },
        { text: 'Last Name', value: 'LastName' },
        { text: 'Title', value: 'Title' },
        { text: 'NPI', value: 'Npi' },
        { text: 'Created On', value: 'CreatedOn', filterable: false },
        { text: 'Is Active', value: 'Active', filterable: false },
        { text: 'Status', value: 'Status' },
        {
          text: 'Actions',
          value: 'Actions',
          sortable: false,
          filterable: false,
        },
      ],
      items: [],
      loading: true,
      search: '',
      page: 1,
    };
  },
  methods: {
    getMedia(id) {
      return mediaHelpers.getMediaUrl(id, 'Provider', 'Provider.jpg');
    },
    updateActiveState({ value, target }) {
      this.$store.dispatch('patch', {
        api: `Provider/${target.Id}/Activate`,
        data: { '': value },
      });
    },
    async downloadPdf(item) {
      const provider = item;
      if (!provider.PdfId) {
        const pdf = await mediaHelpers.generateProviderPdf(item.Id, provider);
        provider.PdfId = pdf.data[0].Id;
        await this.$store.dispatch('put', {
          api: `Provider/${provider.Id}`,
          data: provider,
        });
      }
      window.open(mediaHelpers.getMediaUrl(provider.PdfId), '_blank');
    },
    async deleteItem(item) {
      try {
        const i = this.items.indexOf(item);
        await this.$store.dispatch('delete', `Provider/${item.Id}`);
        this.items.splice(i, 1);
      } catch (e) {
        // Do nothing
      }
    },
  },
  async created() {
    const response = await this.$store.dispatch('get', 'Provider');
    this.items = response.data.Items;
    this.loading = false;
  },
};
</script>
